import type {
  AddCmmdProjectMemberParams,
  CmmdBoardTask,
  CmmdMember,
  CmmdProjectAgentInstance,
  CmmdProjectAgentMember,
  CmmdProjectAgentMemberConsolidationPreview,
  CmmdProjectAgentMemberConsolidationResult,
  CmmdProjectLeadEvents,
  CreateCmmdProjectAgentMemberParams,
} from "@cmmd-forge/contracts";
import { PlusIcon } from "lucide-react";
import { useState } from "react";

import { PersonAvatar } from "~/components/identity/PersonAvatar";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { AddProjectPersonDialog } from "./AddProjectPersonDialog";
import { ProjectAgentMembers } from "./ProjectAgentMembers";
import { formatTeamRole } from "./projectTeamLabels";

export interface CmmdProjectTeamTabProps {
  readonly projectId: string;
  readonly members: readonly CmmdMember[];
  readonly workspaceMembers: readonly CmmdMember[];
  readonly workspaceMembersLoading: boolean;
  readonly workspaceMembersError: string | null;
  readonly agentMembers: readonly CmmdProjectAgentMember[];
  readonly agentMembersLoading: boolean;
  readonly agentMembersError: string | null;
  readonly agentEvents: CmmdProjectLeadEvents["events"];
  readonly agentEventsLoading: boolean;
  readonly agentEventsError: string | null;
  readonly tasks: readonly CmmdBoardTask[];
  readonly onCreateAgentMember: (
    input: Omit<CreateCmmdProjectAgentMemberParams, "idempotencyKey">,
  ) => Promise<void>;
  readonly onAddProjectMember: (input: AddCmmdProjectMemberParams) => Promise<void>;
  readonly onRenameAgentMember: (memberId: number, displayName: string) => Promise<void>;
  readonly onRetireAgentMember: (memberId: number) => Promise<void>;
  readonly onUpdateAgentMemberAvatar: (
    memberId: number,
    avatar: { readonly kind: "generated"; readonly seed: string } | { readonly kind: "default" },
  ) => Promise<void>;
  readonly onListAgentMemberInstances: (
    memberId: number,
  ) => Promise<ReadonlyArray<CmmdProjectAgentInstance>>;
  readonly onPreviewAgentMemberConsolidation: () => Promise<CmmdProjectAgentMemberConsolidationPreview>;
  readonly onApplyAgentMemberConsolidation: (
    expectedPlanHash: string,
  ) => Promise<CmmdProjectAgentMemberConsolidationResult>;
}

export function CmmdProjectTeamTab(props: CmmdProjectTeamTabProps) {
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  return (
    <div className="space-y-10 p-4 sm:p-6">
      <HumanMembers members={props.members} onAdd={() => setAddPersonOpen(true)} />
      <ProjectAgentMembers
        projectId={props.projectId}
        members={props.agentMembers}
        loading={props.agentMembersLoading}
        tasks={props.tasks}
        error={props.agentMembersError}
        events={props.agentEvents}
        eventsLoading={props.agentEventsLoading}
        eventsError={props.agentEventsError}
        onCreate={props.onCreateAgentMember}
        onRename={props.onRenameAgentMember}
        onRetire={props.onRetireAgentMember}
        onUpdateAvatar={props.onUpdateAgentMemberAvatar}
        onListInstances={props.onListAgentMemberInstances}
        onPreviewConsolidation={props.onPreviewAgentMemberConsolidation}
        onApplyConsolidation={props.onApplyAgentMemberConsolidation}
      />
      <AddProjectPersonDialog
        open={addPersonOpen}
        onOpenChange={setAddPersonOpen}
        projectMembers={props.members}
        workspaceMembers={props.workspaceMembers}
        loading={props.workspaceMembersLoading}
        loadError={props.workspaceMembersError}
        onAdd={props.onAddProjectMember}
      />
    </div>
  );
}

function HumanMembers(props: {
  readonly members: readonly CmmdMember[];
  readonly onAdd: () => void;
}) {
  return (
    <section aria-labelledby="project-human-team-heading" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3
              id="project-human-team-heading"
              className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              People
            </h3>
            <span className="text-xs tabular-nums text-muted-foreground/70">
              {props.members.length}
            </span>
          </div>
        </div>
        <Button size="sm" onClick={props.onAdd}>
          <PlusIcon className="size-3.5" aria-hidden />
          Add person
        </Button>
      </div>
      {props.members.length === 0 ? (
        <p className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          No people have been added yet.
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="hidden grid-cols-[minmax(14rem,1fr)_10rem_9rem] gap-3 border-b border-border bg-muted/40 px-3 py-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground md:grid">
            <span>Person</span>
            <span>Role</span>
            <span>Access</span>
          </div>
          <ul className="divide-y divide-border">
            {props.members.map((member) => (
              <li
                key={member.id}
                className="grid min-h-14 grid-cols-[auto_minmax(0,1fr)] items-center gap-3 px-3 py-2 md:grid-cols-[auto_minmax(12rem,1fr)_10rem_9rem]"
              >
                <PersonAvatar
                  name={member.name}
                  avatarUrl={member.avatarUrl}
                  className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-xs font-medium text-muted-foreground"
                />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-foreground">
                    {member.name}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {member.email ?? "CMMD member"}
                  </span>
                </span>
                <span className="hidden md:block">
                  {member.role ? <TeamRoleBadge role={member.role} /> : null}
                </span>
                <span className="hidden text-xs text-muted-foreground md:block">
                  Project member
                </span>
                <span className="col-start-2 text-xs capitalize text-muted-foreground md:hidden">
                  {member.role ?? "member"} · Project member
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function TeamRoleBadge({ role }: { readonly role: string }) {
  const isOwner = role.toLowerCase() === "owner";
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize",
        isOwner
          ? "bg-warning-surface text-warning-foreground ring-1 ring-inset ring-warning-border"
          : "text-muted-foreground",
      )}
    >
      {isOwner ? "Owner" : formatTeamRole(role)}
    </span>
  );
}
